import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { apiRequest, ApiError } from '../utils/apiClient';
import { useAuth } from './useAuth';

export interface MessageParticipant {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email: string;
}

export interface Message {
  id: string;
  threadId: string;
  senderId: string;
  content: string;
  read: boolean;
  createdAt: string;
  sender?: MessageParticipant;
}

export interface MessageThread {
  id: string;
  propertyId: string;
  propertyTitle?: string;
  propertyImage?: string;
  propertyPrice?: number;
  participants: MessageParticipant[];
  lastMessage?: Message;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

interface MessagesState {
  threads: MessageThread[];
  currentThread: MessageThread | null;
  messages: Message[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

interface MessagesContextType extends MessagesState {
  fetchThreads: () => Promise<void>;
  fetchThread: (threadId: string) => Promise<void>;
  sendMessage: (threadId: string, content: string) => Promise<Message | null>;
  createThread: (propertyId: string, message: string) => Promise<MessageThread | null>;
  markAsRead: (threadId: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  clearCurrentThread: () => void;
}

const MessagesContext = createContext<MessagesContextType | undefined>(undefined);

export function MessagesProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const token = session?.token;

  const [state, setState] = useState<MessagesState>({
    threads: [],
    currentThread: null,
    messages: [],
    unreadCount: 0,
    loading: false,
    error: null,
  });

  const refreshUnreadCount = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ unreadCount: number }>('/messages/unread-count', {
        method: 'GET',
        token,
      });
      setState(prev => ({ ...prev, unreadCount: response.unreadCount }));
    } catch (err) {
      console.warn('Failed to fetch unread count:', err);
    }
  }, [token]);

  const fetchThreads = useCallback(async () => {
    if (!token) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiRequest<{ threads: MessageThread[] }>('/messages/threads', {
        method: 'GET',
        token,
      });
      setState(prev => ({
        ...prev,
        threads: response.threads || [],
        loading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load messages';
      setState(prev => ({ ...prev, error: message, loading: false }));
    }
  }, [token]);

  const fetchThread = useCallback(async (threadId: string) => {
    if (!token) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiRequest<{ thread: MessageThread; messages: Message[] }>(
        `/messages/threads/${threadId}`,
        {
          method: 'GET',
          token,
        }
      );
      setState(prev => ({
        ...prev,
        currentThread: response.thread,
        messages: response.messages || [],
        loading: false,
      }));

      // Also refresh the unread count after viewing a thread
      refreshUnreadCount();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load conversation';
      setState(prev => ({ ...prev, error: message, loading: false }));
    }
  }, [token, refreshUnreadCount]);

  const sendMessage = useCallback(async (threadId: string, content: string): Promise<Message | null> => {
    if (!token || !content.trim()) return null;

    try {
      const response = await apiRequest<{ message: Message }>(
        `/messages/threads/${threadId}/messages`,
        {
          method: 'POST',
          token,
          body: { content: content.trim() },
        }
      );

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, response.message],
        threads: prev.threads.map(t =>
          t.id === threadId
            ? { ...t, lastMessage: response.message, updatedAt: response.message.createdAt }
            : t
        ),
      }));

      return response.message;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to send message';
      setState(prev => ({ ...prev, error: message }));
      return null;
    }
  }, [token]);

  const createThread = useCallback(async (propertyId: string, message: string): Promise<MessageThread | null> => {
    if (!token || !message.trim()) return null;

    try {
      const response = await apiRequest<{ thread: MessageThread; message: Message }>(
        '/messages/threads',
        {
          method: 'POST',
          token,
          body: { propertyId, message: message.trim() },
        }
      );

      setState(prev => ({
        ...prev,
        threads: [response.thread, ...prev.threads],
        currentThread: response.thread,
        messages: [response.message],
      }));

      return response.thread;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to start conversation';
      setState(prev => ({ ...prev, error: message }));
      return null;
    }
  }, [token]);

  const markAsRead = useCallback(async (threadId: string) => {
    if (!token) return;

    try {
      await apiRequest(`/messages/threads/${threadId}/read`, {
        method: 'POST',
        token,
      });

      setState(prev => ({
        ...prev,
        threads: prev.threads.map(t =>
          t.id === threadId ? { ...t, unreadCount: 0 } : t
        ),
        messages: prev.messages.map(m => ({ ...m, read: true })),
      }));

      refreshUnreadCount();
    } catch (err) {
      console.warn('Failed to mark as read:', err);
    }
  }, [token, refreshUnreadCount]);

  const clearCurrentThread = useCallback(() => {
    setState(prev => ({ ...prev, currentThread: null, messages: [] }));
  }, []);

  // Fetch unread count on mount and when token changes
  useEffect(() => {
    if (token) {
      refreshUnreadCount();
    }
  }, [token, refreshUnreadCount]);

  // Poll for new messages every 30 seconds when logged in
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [token, refreshUnreadCount]);

  const value = useMemo<MessagesContextType>(
    () => ({
      ...state,
      fetchThreads,
      fetchThread,
      sendMessage,
      createThread,
      markAsRead,
      refreshUnreadCount,
      clearCurrentThread,
    }),
    [state, fetchThreads, fetchThread, sendMessage, createThread, markAsRead, refreshUnreadCount, clearCurrentThread]
  );

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessages(): MessagesContextType {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }
  return context;
}
